/**
 * Cross-File Consistency Analyzer
 * 
 * THE MOST COMPREHENSIVE CROSS-FILE VERIFICATION:
 * - Tracks type definitions across files
 * - Validates import/export consistency
 * - Detects circular dependencies
 * - Verifies interface implementations
 * - Checks for duplicate definitions
 * - Analyzes call graph for dead code
 */

import * as fs from 'fs';
import * as path from 'path';

export interface FileAnalysis {
  path: string;
  exports: CFExportInfo[];
  imports: CFImportInfo[];
  types: TypeDefinition[];
  functions: FunctionDefinition[];
  dependencies: string[];
  dependents: string[];
}

export interface CFExportInfo {
  name: string;
  type: 'function' | 'class' | 'type' | 'interface' | 'const' | 'let' | 'var' | 'enum' | 'default';
  isReExport: boolean;
  originalSource?: string;
  line: number;
}

export interface CFImportInfo {
  source: string;
  specifiers: { name: string; alias?: string; isDefault: boolean }[];
  isTypeOnly: boolean;
  line: number;
  resolved: boolean;
  resolvedPath?: string;
}

export interface TypeDefinition {
  name: string;
  kind: 'interface' | 'type' | 'class' | 'enum';
  properties: { name: string; type: string; optional: boolean }[];
  methods: { name: string; params: string; returnType: string }[];
  extends?: string[];
  implements?: string[];
  line: number;
}

export interface FunctionDefinition {
  name: string;
  params: { name: string; type: string; optional: boolean }[];
  returnType: string;
  isAsync: boolean;
  isExported: boolean;
  line: number;
  calls: string[];
  calledBy: string[];
}

export interface ConsistencyIssue {
  type: 'missing_export' | 'missing_import' | 'type_mismatch' | 'circular_dependency' | 
        'duplicate_definition' | 'dead_code' | 'inconsistent_signature' | 'missing_implementation';
  severity: 'error' | 'warning' | 'info';
  file: string;
  line?: number;
  message: string;
  relatedFiles?: string[];
  fix?: string;
}

export interface CrossFileAnalysisResult {
  files: Map<string, FileAnalysis>;
  issues: ConsistencyIssue[];
  dependencyGraph: Map<string, Set<string>>;
  circularDependencies: string[][];
  unusedExports: { file: string; export: string }[];
  missingTypes: { file: string; type: string; referencedIn: string[] }[];
  duplicateDefinitions: { name: string; files: string[] }[];
}

export class CrossFileAnalyzer {
  private workspaceRoot: string;
  private fileCache: Map<string, FileAnalysis> = new Map();
  private typeRegistry: Map<string, { file: string; definition: TypeDefinition }[]> = new Map();
  private exportRegistry: Map<string, { file: string; export: CFExportInfo }[]> = new Map();

  constructor(workspaceRoot: string) {
    this.workspaceRoot = workspaceRoot;
  }

  /**
   * Analyze multiple files for cross-file consistency
   */
  async analyzeProject(files: string[]): Promise<CrossFileAnalysisResult> {
    const fileAnalyses = new Map<string, FileAnalysis>();
    const issues: ConsistencyIssue[] = [];

    // Phase 1: Analyze each file
    for (const file of files) {
      try {
        const analysis = await this.analyzeFile(file);
        fileAnalyses.set(file, analysis);
        this.fileCache.set(file, analysis);
      } catch (e) {
        issues.push({
          type: 'missing_import',
          severity: 'error',
          file,
          message: `Failed to analyze file: ${e}`,
        });
      }
    }

    // Phase 2: Build registries
    this.buildTypeRegistry(fileAnalyses);
    this.buildExportRegistry(fileAnalyses);

    // Phase 3: Build dependency graph
    const dependencyGraph = this.buildDependencyGraph(fileAnalyses);

    // Phase 4: Detect circular dependencies
    const circularDependencies = this.detectCircularDependencies(dependencyGraph);
    for (const cycle of circularDependencies) {
      issues.push({
        type: 'circular_dependency',
        severity: 'warning',
        file: cycle[0],
        message: `Circular dependency detected: ${cycle.join(' -> ')}`,
        relatedFiles: cycle,
        fix: 'Consider extracting shared code to a separate module',
      });
    }

    // Phase 5: Validate imports
    issues.push(...this.validateImports(fileAnalyses));

    // Phase 6: Check for duplicate definitions
    const duplicateDefinitions = this.findDuplicateDefinitions();
    for (const dup of duplicateDefinitions) {
      issues.push({
        type: 'duplicate_definition',
        severity: 'warning',
        file: dup.files[0],
        message: `"${dup.name}" is defined in multiple files: ${dup.files.join(', ')}`,
        relatedFiles: dup.files,
        fix: 'Consider consolidating to a single definition',
      });
    }

    // Phase 7: Find unused exports
    const unusedExports = this.findUnusedExports(fileAnalyses);
    for (const unused of unusedExports) {
      issues.push({
        type: 'dead_code',
        severity: 'info',
        file: unused.file,
        message: `Export "${unused.export}" is not imported by any other file`,
        fix: 'Consider removing if not used externally',
      });
    }

    // Phase 8: Find missing types
    const missingTypes = this.findMissingTypes(fileAnalyses);
    for (const missing of missingTypes) {
      issues.push({
        type: 'type_mismatch',
        severity: 'error',
        file: missing.file,
        message: `Type "${missing.type}" is used but not defined or imported`,
        relatedFiles: missing.referencedIn,
        fix: `Import "${missing.type}" or define it locally`,
      });
    }

    // Phase 9: Validate interface implementations
    issues.push(...this.validateImplementations(fileAnalyses));

    return {
      files: fileAnalyses,
      issues,
      dependencyGraph,
      circularDependencies,
      unusedExports,
      missingTypes,
      duplicateDefinitions,
    };
  }

  /**
   * Analyze a single file
   */
  async analyzeFile(filePath: string): Promise<FileAnalysis> {
    const absolutePath = path.isAbsolute(filePath) 
      ? filePath 
      : path.resolve(this.workspaceRoot, filePath);
    
    const content = fs.readFileSync(absolutePath, 'utf-8');
    const lines = content.split('\n');

    const exports = this.extractExports(content, lines);
    const imports = this.extractImports(content, lines, absolutePath);
    const types = this.extractTypes(content, lines);
    const functions = this.extractFunctions(content, lines);

    // Resolve dependencies
    const dependencies = imports.map(i => i.resolvedPath || i.source).filter(Boolean);
    
    return {
      path: absolutePath,
      exports,
      imports,
      types,
      functions,
      dependencies,
      dependents: [], // Will be filled in during cross-file analysis
    };
  }

  /**
   * Validate code snippet against project context
   */
  validateSnippet(code: string, targetFile: string): ConsistencyIssue[] {
    const issues: ConsistencyIssue[] = [];
    const lines = code.split('\n');

    // Extract imports from snippet
    const snippetImports = this.extractImports(code, lines, targetFile);

    // Check each import against project
    for (const imp of snippetImports) {
      // Check if import source exists
      if (imp.source.startsWith('.')) {
        const resolvedPath = this.resolveImportPath(imp.source, targetFile);
        if (!resolvedPath || !this.fileCache.has(resolvedPath)) {
          issues.push({
            type: 'missing_import',
            severity: 'error',
            file: targetFile,
            line: imp.line,
            message: `Import source "${imp.source}" does not exist`,
            fix: 'Check the file path is correct',
          });
          continue;
        }

        // Check if specifiers exist in source file
        const sourceAnalysis = this.fileCache.get(resolvedPath);
        if (sourceAnalysis) {
          for (const spec of imp.specifiers) {
            const exportExists = sourceAnalysis.exports.some(e => 
              e.name === spec.name || (spec.isDefault && e.type === 'default')
            );
            if (!exportExists) {
              issues.push({
                type: 'missing_export',
                severity: 'error',
                file: targetFile,
                line: imp.line,
                message: `"${spec.name}" is not exported from "${imp.source}"`,
                relatedFiles: [resolvedPath],
                fix: `Add export for "${spec.name}" in ${path.basename(resolvedPath)}`,
              });
            }
          }
        }
      }
    }

    // Extract type references and validate
    const typeRefs = this.extractTypeReferences(code);
    for (const typeRef of typeRefs) {
      // Check if type is defined locally or imported
      const isImported = snippetImports.some(i => 
        i.specifiers.some(s => s.name === typeRef || s.alias === typeRef)
      );
      const isDefinedLocally = code.includes(`interface ${typeRef}`) || 
                               code.includes(`type ${typeRef}`) ||
                               code.includes(`class ${typeRef}`) ||
                               code.includes(`enum ${typeRef}`);

      if (!isImported && !isDefinedLocally && !this.isBuiltinType(typeRef)) {
        // Check if it exists in the project
        const existsInProject = this.typeRegistry.has(typeRef);
        if (!existsInProject) {
          issues.push({
            type: 'type_mismatch',
            severity: 'warning',
            file: targetFile,
            message: `Type "${typeRef}" is used but may not be defined in the project`,
            fix: `Define "${typeRef}" or import it from a module`,
          });
        }
      }
    }

    // Extract function calls and validate
    const functionCalls = this.extractFunctionCalls(code);
    for (const call of functionCalls) {
      // Check if function is imported or defined locally
      const isImported = snippetImports.some(i =>
        i.specifiers.some(s => s.name === call || s.alias === call)
      );
      const isDefinedLocally = new RegExp(`function\\s+${call}\\s*\\(`).test(code) ||
                               new RegExp(`const\\s+${call}\\s*=`).test(code);

      if (!isImported && !isDefinedLocally && !this.isBuiltinFunction(call)) {
        issues.push({
          type: 'missing_import',
          severity: 'warning',
          file: targetFile,
          message: `Function "${call}" is called but may not be imported or defined`,
          fix: `Import "${call}" or define it locally`,
        });
      }
    }

    return issues;
  }

  // Extraction methods

  private extractExports(content: string, lines: string[]): CFExportInfo[] {
    const exports: CFExportInfo[] = [];

    // Named exports
    const namedExportRegex = /export\s+(?:async\s+)?(?:function|const|let|var|class|interface|type|enum)\s+(\w+)/g;
    let match;
    while ((match = namedExportRegex.exec(content)) !== null) {
      const type = this.getExportType(content, match.index);
      exports.push({
        name: match[1],
        type,
        isReExport: false,
        line: this.getLineNumber(content, match.index),
      });
    }

    // Default exports
    const defaultExportRegex = /export\s+default\s+(?:async\s+)?(?:function\s+)?(\w+)?/g;
    while ((match = defaultExportRegex.exec(content)) !== null) {
      exports.push({
        name: match[1] || 'default',
        type: 'default',
        isReExport: false,
        line: this.getLineNumber(content, match.index),
      });
    }

    // Re-exports
    const reExportRegex = /export\s+\{([^}]+)\}\s+from\s+['"]([^'"]+)['"]/g;
    while ((match = reExportRegex.exec(content)) !== null) {
      const names = match[1].split(',').map(s => s.trim().split(/\s+as\s+/)[0]);
      for (const name of names) {
        if (name) {
          exports.push({
            name,
            type: 'const', // Default, would need more analysis
            isReExport: true,
            originalSource: match[2],
            line: this.getLineNumber(content, match.index),
          });
        }
      }
    }

    // Export all
    const exportAllRegex = /export\s+\*\s+(?:as\s+(\w+)\s+)?from\s+['"]([^'"]+)['"]/g;
    while ((match = exportAllRegex.exec(content)) !== null) {
      exports.push({
        name: match[1] || '*',
        type: 'const',
        isReExport: true,
        originalSource: match[2],
        line: this.getLineNumber(content, match.index),
      });
    }

    return exports;
  }

  private extractImports(content: string, lines: string[], fromFile: string): CFImportInfo[] {
    const imports: CFImportInfo[] = [];

    const importRegex = /import\s+(?:type\s+)?(?:(\{[^}]+\})|(\*\s+as\s+\w+)|(\w+))?\s*(?:,\s*(?:(\{[^}]+\})|(\w+)))?\s*from\s+['"]([^'"]+)['"]/g;
    
    let match;
    while ((match = importRegex.exec(content)) !== null) {
      const [fullMatch, namedImports, namespaceImport, defaultImport, additionalNamed, additionalDefault, source] = match;
      const isTypeOnly = fullMatch.includes('import type');
      
      const specifiers: CFImportInfo['specifiers'] = [];

      if (namedImports) {
        const names = namedImports.replace(/[{}]/g, '').split(',').map(s => s.trim());
        for (const name of names) {
          if (name) {
            const [importName, alias] = name.split(/\s+as\s+/).map(s => s.trim());
            specifiers.push({ name: importName, alias, isDefault: false });
          }
        }
      }

      if (namespaceImport) {
        const alias = namespaceImport.replace('* as', '').trim();
        specifiers.push({ name: '*', alias, isDefault: false });
      }

      if (defaultImport) {
        specifiers.push({ name: defaultImport, isDefault: true });
      }

      if (additionalNamed) {
        const names = additionalNamed.replace(/[{}]/g, '').split(',').map(s => s.trim());
        for (const name of names) {
          if (name) {
            const [importName, alias] = name.split(/\s+as\s+/).map(s => s.trim());
            specifiers.push({ name: importName, alias, isDefault: false });
          }
        }
      }

      if (additionalDefault) {
        specifiers.push({ name: additionalDefault, isDefault: true });
      }

      const resolvedPath = this.resolveImportPath(source, fromFile);

      imports.push({
        source,
        specifiers,
        isTypeOnly,
        line: this.getLineNumber(content, match.index),
        resolved: !!resolvedPath,
        resolvedPath,
      });
    }

    return imports;
  }

  private extractTypes(content: string, lines: string[]): TypeDefinition[] {
    const types: TypeDefinition[] = [];

    // Interfaces
    const interfaceRegex = /interface\s+(\w+)(?:<[^>]+>)?(?:\s+extends\s+([^{]+))?\s*\{/g;
    let match;
    while ((match = interfaceRegex.exec(content)) !== null) {
      const [, name, extendsClause] = match;
      const body = this.extractBraceContent(content, match.index + match[0].length - 1);
      
      types.push({
        name,
        kind: 'interface',
        properties: this.parseTypeProperties(body),
        methods: this.parseTypeMethods(body),
        extends: extendsClause?.split(',').map(s => s.trim()),
        line: this.getLineNumber(content, match.index),
      });
    }

    // Type aliases
    const typeRegex = /type\s+(\w+)(?:<[^>]+>)?\s*=/g;
    while ((match = typeRegex.exec(content)) !== null) {
      types.push({
        name: match[1],
        kind: 'type',
        properties: [],
        methods: [],
        line: this.getLineNumber(content, match.index),
      });
    }

    // Classes
    const classRegex = /class\s+(\w+)(?:<[^>]+>)?(?:\s+extends\s+(\w+))?(?:\s+implements\s+([^{]+))?\s*\{/g;
    while ((match = classRegex.exec(content)) !== null) {
      const [, name, extendsClass, implementsClause] = match;
      const body = this.extractBraceContent(content, match.index + match[0].length - 1);

      types.push({
        name,
        kind: 'class',
        properties: this.parseClassProperties(body),
        methods: this.parseClassMethods(body),
        extends: extendsClass ? [extendsClass] : undefined,
        implements: implementsClause?.split(',').map(s => s.trim()),
        line: this.getLineNumber(content, match.index),
      });
    }

    // Enums
    const enumRegex = /enum\s+(\w+)\s*\{/g;
    while ((match = enumRegex.exec(content)) !== null) {
      types.push({
        name: match[1],
        kind: 'enum',
        properties: [],
        methods: [],
        line: this.getLineNumber(content, match.index),
      });
    }

    return types;
  }

  private extractFunctions(content: string, lines: string[]): FunctionDefinition[] {
    const functions: FunctionDefinition[] = [];

    // Regular functions
    const funcRegex = /(?:export\s+)?(?:async\s+)?function\s*\*?\s*(\w+)\s*\(([^)]*)\)(?:\s*:\s*([^\{]+))?\s*\{/g;
    let match;
    while ((match = funcRegex.exec(content)) !== null) {
      const [fullMatch, name, params, returnType] = match;
      const body = this.extractBraceContent(content, match.index + fullMatch.length - 1);

      functions.push({
        name,
        params: this.parseParams(params),
        returnType: returnType?.trim() || 'void',
        isAsync: fullMatch.includes('async'),
        isExported: fullMatch.includes('export'),
        line: this.getLineNumber(content, match.index),
        calls: this.extractFunctionCalls(body),
        calledBy: [],
      });
    }

    // Arrow functions assigned to const
    const arrowRegex = /(?:export\s+)?const\s+(\w+)\s*(?::\s*([^=]+))?\s*=\s*(?:async\s*)?\([^)]*\)\s*(?::\s*([^\s=>]+))?\s*=>/g;
    while ((match = arrowRegex.exec(content)) !== null) {
      const [fullMatch, name, typeAnnotation, returnType] = match;
      
      functions.push({
        name,
        params: [],
        returnType: returnType?.trim() || typeAnnotation?.trim() || 'unknown',
        isAsync: fullMatch.includes('async'),
        isExported: fullMatch.includes('export'),
        line: this.getLineNumber(content, match.index),
        calls: [],
        calledBy: [],
      });
    }

    return functions;
  }

  // Analysis methods

  private buildTypeRegistry(files: Map<string, FileAnalysis>): void {
    this.typeRegistry.clear();
    
    for (const [filePath, analysis] of files) {
      for (const type of analysis.types) {
        if (!this.typeRegistry.has(type.name)) {
          this.typeRegistry.set(type.name, []);
        }
        this.typeRegistry.get(type.name)!.push({ file: filePath, definition: type });
      }
    }
  }

  private buildExportRegistry(files: Map<string, FileAnalysis>): void {
    this.exportRegistry.clear();

    for (const [filePath, analysis] of files) {
      for (const exp of analysis.exports) {
        if (!this.exportRegistry.has(exp.name)) {
          this.exportRegistry.set(exp.name, []);
        }
        this.exportRegistry.get(exp.name)!.push({ file: filePath, export: exp });
      }
    }
  }

  private buildDependencyGraph(files: Map<string, FileAnalysis>): Map<string, Set<string>> {
    const graph = new Map<string, Set<string>>();

    for (const [filePath, analysis] of files) {
      const deps = new Set<string>();
      
      for (const imp of analysis.imports) {
        if (imp.resolvedPath && files.has(imp.resolvedPath)) {
          deps.add(imp.resolvedPath);
        }
      }

      graph.set(filePath, deps);
    }

    // Update dependents
    for (const [filePath, deps] of graph) {
      for (const dep of deps) {
        const depAnalysis = files.get(dep);
        if (depAnalysis) {
          depAnalysis.dependents.push(filePath);
        }
      }
    }

    return graph;
  }

  private detectCircularDependencies(graph: Map<string, Set<string>>): string[][] {
    const cycles: string[][] = [];
    const visited = new Set<string>();
    const recursionStack = new Set<string>();

    const dfs = (node: string, currentPath: string[]): boolean => {
      visited.add(node);
      recursionStack.add(node);
      currentPath.push(node);

      const deps = graph.get(node) || new Set();
      for (const dep of deps) {
        if (!visited.has(dep)) {
          if (dfs(dep, currentPath)) return true;
        } else if (recursionStack.has(dep)) {
          // Found cycle
          const cycleStart = currentPath.indexOf(dep);
          const cycle = currentPath.slice(cycleStart);
          cycle.push(dep); // Complete the cycle
          cycles.push(cycle.map((p: string) => p.replace(this.workspaceRoot, '').replace(/^[/\\]/, '')));
        }
      }

      currentPath.pop();
      recursionStack.delete(node);
      return false;
    };

    for (const node of graph.keys()) {
      if (!visited.has(node)) {
        dfs(node, []);
      }
    }

    return cycles;
  }

  private validateImports(files: Map<string, FileAnalysis>): ConsistencyIssue[] {
    const issues: ConsistencyIssue[] = [];

    for (const [filePath, analysis] of files) {
      for (const imp of analysis.imports) {
        // Check if import can be resolved
        if (!imp.resolved && imp.source.startsWith('.')) {
          issues.push({
            type: 'missing_import',
            severity: 'error',
            file: filePath,
            line: imp.line,
            message: `Cannot resolve import "${imp.source}"`,
            fix: 'Check the file path is correct',
          });
          continue;
        }

        // Check if specifiers exist in source
        if (imp.resolvedPath && files.has(imp.resolvedPath)) {
          const sourceFile = files.get(imp.resolvedPath)!;
          
          for (const spec of imp.specifiers) {
            if (spec.isDefault) {
              const hasDefault = sourceFile.exports.some(e => e.type === 'default');
              if (!hasDefault) {
                issues.push({
                  type: 'missing_export',
                  severity: 'error',
                  file: filePath,
                  line: imp.line,
                  message: `No default export in "${imp.source}"`,
                  relatedFiles: [imp.resolvedPath],
                });
              }
            } else if (spec.name !== '*') {
              const hasExport = sourceFile.exports.some(e => e.name === spec.name);
              if (!hasExport) {
                issues.push({
                  type: 'missing_export',
                  severity: 'error',
                  file: filePath,
                  line: imp.line,
                  message: `"${spec.name}" is not exported from "${imp.source}"`,
                  relatedFiles: [imp.resolvedPath],
                  fix: `Add "export" to "${spec.name}" in ${path.basename(imp.resolvedPath)}`,
                });
              }
            }
          }
        }
      }
    }

    return issues;
  }

  private findDuplicateDefinitions(): { name: string; files: string[] }[] {
    const duplicates: { name: string; files: string[] }[] = [];

    for (const [name, definitions] of this.typeRegistry) {
      if (definitions.length > 1) {
        duplicates.push({
          name,
          files: definitions.map(d => d.file),
        });
      }
    }

    return duplicates;
  }

  private findUnusedExports(files: Map<string, FileAnalysis>): { file: string; export: string }[] {
    const unused: { file: string; export: string }[] = [];
    const usedExports = new Set<string>();

    // Collect all used exports
    for (const analysis of files.values()) {
      for (const imp of analysis.imports) {
        for (const spec of imp.specifiers) {
          usedExports.add(`${imp.resolvedPath || imp.source}:${spec.name}`);
        }
      }
    }

    // Find unused
    for (const [filePath, analysis] of files) {
      for (const exp of analysis.exports) {
        const key = `${filePath}:${exp.name}`;
        if (!usedExports.has(key) && exp.name !== 'default') {
          unused.push({ file: filePath, export: exp.name });
        }
      }
    }

    return unused;
  }

  private findMissingTypes(files: Map<string, FileAnalysis>): { file: string; type: string; referencedIn: string[] }[] {
    // Would need full type reference tracking
    return [];
  }

  private validateImplementations(files: Map<string, FileAnalysis>): ConsistencyIssue[] {
    const issues: ConsistencyIssue[] = [];

    for (const [filePath, analysis] of files) {
      for (const type of analysis.types) {
        if (type.kind === 'class' && type.implements) {
          for (const interfaceName of type.implements) {
            const interfaceDefs = this.typeRegistry.get(interfaceName);
            if (!interfaceDefs) {
              issues.push({
                type: 'missing_implementation',
                severity: 'warning',
                file: filePath,
                line: type.line,
                message: `Class "${type.name}" implements "${interfaceName}" which is not found`,
              });
            }
            // Would need deeper analysis to check all members are implemented
          }
        }
      }
    }

    return issues;
  }

  // Helper methods

  private resolveImportPath(source: string, fromFile: string): string | undefined {
    if (!source.startsWith('.')) return undefined;

    const fromDir = path.dirname(fromFile);
    const extensions = ['.ts', '.tsx', '.js', '.jsx', ''];
    
    for (const ext of extensions) {
      const tryPath = path.resolve(fromDir, source + ext);
      if (fs.existsSync(tryPath)) return tryPath;
      
      const indexPath = path.join(path.resolve(fromDir, source), 'index' + ext);
      if (fs.existsSync(indexPath)) return indexPath;
    }

    return undefined;
  }

  private getExportType(content: string, index: number): CFExportInfo['type'] {
    const slice = content.slice(index, index + 50);
    if (/^export\s+(?:async\s+)?function/.test(slice)) return 'function';
    if (/^export\s+class/.test(slice)) return 'class';
    if (/^export\s+interface/.test(slice)) return 'interface';
    if (/^export\s+type/.test(slice)) return 'type';
    if (/^export\s+enum/.test(slice)) return 'enum';
    if (/^export\s+const/.test(slice)) return 'const';
    if (/^export\s+let/.test(slice)) return 'let';
    if (/^export\s+var/.test(slice)) return 'var';
    return 'const';
  }

  private getLineNumber(content: string, index: number): number {
    return content.slice(0, index).split('\n').length;
  }

  private extractBraceContent(content: string, startIndex: number): string {
    let depth = 0;
    let started = false;
    let endIndex = startIndex;

    for (let i = startIndex; i < content.length; i++) {
      if (content[i] === '{') {
        depth++;
        started = true;
      } else if (content[i] === '}') {
        depth--;
        if (started && depth === 0) {
          endIndex = i;
          break;
        }
      }
    }

    return content.slice(startIndex + 1, endIndex);
  }

  private parseParams(params: string): { name: string; type: string; optional: boolean }[] {
    if (!params.trim()) return [];
    
    return params.split(',').map(p => {
      const parts = p.trim().split(':');
      const namePart = parts[0].trim();
      const optional = namePart.includes('?');
      const name = namePart.replace(/[?]/g, '').trim();
      const type = parts[1]?.trim() || 'any';
      return { name, type, optional };
    });
  }

  private parseTypeProperties(body: string): { name: string; type: string; optional: boolean }[] {
    const props: { name: string; type: string; optional: boolean }[] = [];
    const propRegex = /(\w+)(\?)?:\s*([^;]+);/g;
    
    let match;
    while ((match = propRegex.exec(body)) !== null) {
      props.push({
        name: match[1],
        type: match[3].trim(),
        optional: match[2] === '?',
      });
    }

    return props;
  }

  private parseTypeMethods(body: string): { name: string; params: string; returnType: string }[] {
    const methods: { name: string; params: string; returnType: string }[] = [];
    const methodRegex = /(\w+)\s*\(([^)]*)\)(?:\s*:\s*([^;]+))?;/g;

    let match;
    while ((match = methodRegex.exec(body)) !== null) {
      methods.push({
        name: match[1],
        params: match[2],
        returnType: match[3]?.trim() || 'void',
      });
    }

    return methods;
  }

  private parseClassProperties(body: string): { name: string; type: string; optional: boolean }[] {
    return this.parseTypeProperties(body);
  }

  private parseClassMethods(body: string): { name: string; params: string; returnType: string }[] {
    const methods: { name: string; params: string; returnType: string }[] = [];
    const methodRegex = /(?:public|private|protected)?\s*(?:async\s+)?(\w+)\s*\(([^)]*)\)(?:\s*:\s*([^\{]+))?\s*\{/g;

    let match;
    while ((match = methodRegex.exec(body)) !== null) {
      methods.push({
        name: match[1],
        params: match[2],
        returnType: match[3]?.trim() || 'void',
      });
    }

    return methods;
  }

  private extractTypeReferences(code: string): string[] {
    const refs: string[] = [];
    const typeRefRegex = /:\s*([A-Z][a-zA-Z0-9]*)/g;
    
    let match;
    while ((match = typeRefRegex.exec(code)) !== null) {
      refs.push(match[1]);
    }

    return [...new Set(refs)];
  }

  private extractFunctionCalls(code: string): string[] {
    const calls: string[] = [];
    const callRegex = /\b([a-zA-Z_]\w*)\s*\(/g;

    let match;
    while ((match = callRegex.exec(code)) !== null) {
      const name = match[1];
      if (!this.isKeyword(name) && !this.isBuiltinFunction(name)) {
        calls.push(name);
      }
    }

    return [...new Set(calls)];
  }

  private isBuiltinType(name: string): boolean {
    const builtins = new Set([
      'string', 'number', 'boolean', 'object', 'any', 'unknown', 'never', 'void',
      'null', 'undefined', 'symbol', 'bigint', 'Array', 'Object', 'String',
      'Number', 'Boolean', 'Function', 'Promise', 'Map', 'Set', 'WeakMap',
      'WeakSet', 'Date', 'RegExp', 'Error', 'Buffer', 'Partial', 'Required',
      'Readonly', 'Record', 'Pick', 'Omit', 'Exclude', 'Extract', 'NonNullable',
      'Parameters', 'ReturnType', 'InstanceType', 'Awaited', 'T', 'K', 'V', 'U',
    ]);
    return builtins.has(name);
  }

  private isBuiltinFunction(name: string): boolean {
    const builtins = new Set([
      'console', 'setTimeout', 'setInterval', 'clearTimeout', 'clearInterval',
      'fetch', 'parseInt', 'parseFloat', 'isNaN', 'isFinite', 'encodeURI',
      'decodeURI', 'encodeURIComponent', 'decodeURIComponent', 'eval', 'alert',
      'confirm', 'prompt', 'require', 'import', 'export', 'JSON', 'Math',
      'Date', 'Array', 'Object', 'String', 'Number', 'Boolean', 'Promise',
      'Map', 'Set', 'WeakMap', 'WeakSet', 'Error', 'TypeError', 'RangeError',
      'if', 'for', 'while', 'switch', 'try', 'catch', 'return', 'throw',
    ]);
    return builtins.has(name);
  }

  private isKeyword(word: string): boolean {
    const keywords = new Set([
      'break', 'case', 'catch', 'continue', 'debugger', 'default', 'delete',
      'do', 'else', 'finally', 'for', 'function', 'if', 'in', 'instanceof',
      'new', 'return', 'switch', 'this', 'throw', 'try', 'typeof', 'var',
      'void', 'while', 'with', 'class', 'const', 'enum', 'export', 'extends',
      'import', 'super', 'implements', 'interface', 'let', 'package', 'private',
      'protected', 'public', 'static', 'yield', 'async', 'await',
    ]);
    return keywords.has(word);
  }
}

export function createCrossFileAnalyzer(workspaceRoot: string): CrossFileAnalyzer {
  return new CrossFileAnalyzer(workspaceRoot);
}
